<template>
  <div class="ca-footer-wrap px-size-0 pt-size-6">
    <footer class="ca-footer" aria-label="Rodapé">
      <IconFolha class="ca-footer__folha" />
      <div class="ca-bullet-container">
        <CaFooterMenu :columns="menuColumns" />

        <div class="ca-footer__divider mx-size-0 my-size-6"></div>

        <CaFooterQuickAccess :links="quickLinks" />

        <CaFooterBrand :socialLinks="socialLinks" :appLink="appLink" :logoUrl="logoUrl" />

        <div
          class="ca-footer__divider mx-size-0 my-size-6 ca-footer__divider--desktop"
        ></div>

        <CaFooterCopyright
          :legalText="legalText"
          :languageOptions="languageOptions"
          :currentLanguage="currentLanguage"
        />
      </div>
    </footer>
  </div>
</template>
<script>
const isDev = import.meta.env.DEV;
</script>
<script setup>
import CaFooterMenu from "./CaFooterMenu.vue";
import CaFooterQuickAccess from "./CaFooterQuickAccess.vue";
import CaFooterBrand from "./CaFooterBrand.vue";
import CaFooterCopyright from "./CaFooterCopyright.vue";
import IconFolha from "@assets/Folha.svg";

import { ref, computed, onMounted } from "vue";

const props = defineProps({
  menuColumns: {
    type: Array,
    default: null,
  },

  quickLinks: {
    type: Array,
    default: null,
  },

  socialLinks: {
    type: Array,
    default: null,
  },

  appLink: {
    type: Object,
    default: null,
  },

  logoUrl: {
    type: String,
    default: "/particulares",
  },

  legalText: {
    type: String,
    default:
      "\u00a9 2026 Cr\u00e9dito Agr\u00edcola  \u2014  Todos os Direitos Reservados. Caixa Central de Cr\u00e9dito Agr\u00edcola M\u00fatuo, C.R.L. registada junto do Banco de Portugal sob o n\u00ba 9000.",
  },

  languageOptions: {
    type: Array,
    default: () => [
      { label: "Portugu\u00eas", value: "Portuguese", url: "#" },
      { label: "English", value: "English", url: "#" },
    ],
  },

  currentLanguage: {
    type: String,
    default: "Portuguese",
  },
});

const columnsRef = ref(props.menuColumns ?? null);
const menuColumns = computed(() => columnsRef.value || []);
const quickLinksRef = ref(props.quickLinks ?? null);
const quickLinks = computed(() => quickLinksRef.value || []);
const socialLinksRef = ref(props.socialLinks ?? null);
const socialLinks = computed(() => socialLinksRef.value || []);
const appLinkRef = ref(props.appLink ?? null);
const appLink = computed(() => appLinkRef.value || {});

const loadFooterMocks = async () => {
  if (!isDev) return;

  const { menuColumns, quickLinks, socialLinks, appLink } =
    await import("./mocks/footerMocks.js");

  const map = [
    [columnsRef, menuColumns],
    [quickLinksRef, quickLinks],
    [socialLinksRef, socialLinks],
    [appLinkRef, appLink],
  ];

  for (const [ref, value] of map) {
    if (!ref.value) ref.value = value;
  }
};

onMounted(loadFooterMocks);
</script>

<style scoped lang="scss">
.ca-footer-wrap {
  position: relative;
  z-index: 1;
  background-color: var(--ca-color-text-body);
  /* Whatever sits above the footer ends on a fractional y as often as not, and
     the two boxes then round apart and leave a hairline of the page showing
     between them. Closing it here rather than in each preceding module works
     regardless of what that module is, and costs nothing: the pixel this eats
     is the wrap's own flat padding, well above the rounded corners below. */
  margin-top: -1px;
}

.ca-footer {
  background-color: var(--ca-color-text-body);
  color: var(--ca-color-text-inverted);
  position: relative;
  overflow: hidden;
  border-radius: var(--size-6) var(--size-6) 0 0; // 32px — Figma: top corners arredondados
  padding-bottom: var(--size-6);
}

.ca-footer__folha {
  display: none;
  position: absolute;
  right: -204px;
  bottom: -40px;
  width: 545.835px;
  height: 480px;
  pointer-events: none;

  @media (min-width: 1025px) {
    display: block;
  }
}

.ca-footer__divider {
  align-self: stretch;
  border-top: 0.5px solid var(--ca-color-text-light);
}

.ca-footer__divider--desktop {
  display: none;

  @media (min-width: 1025px) {
    display: block;
  }
}
</style>
